<template>
    <SoliForm
        ref="form"
        v-model="valid"
        :title="
            editing
                ? `Edit ${product.name}`
                : `Create ${form.category?.singular ?? 'Product'}`
        "
        :saveChanges="editing ? edited : true"
        :imgSrc="getImageSource"
        persistent
        @success="$emit('success')"
        @reset="$emit('reset')"
        @submit="submit"
    >
        <template #body>
            <div
                :class="{ 'mt-4 py-2': !!getImageSource }"
                class="flex flex-col gap-4"
            >
                <v-select
                    v-if="false"
                    v-model="form.category"
                    label="Category"
                    prepend-inner-icon="category"
                    item-text="title"
                    item-value="title"
                    return-object
                    :items="[
                        {
                            title: 'Books',
                            singular: 'A Book',
                        },
                        {
                            title: 'Merch',
                            singular: 'Merchandise',
                        },
                    ]"
                    :rules="rules.select"
                    hideDetails="auto"
                />
                <SoliText
                    v-model="form.name"
                    :disabled="!!product?.id && !product?.physical"
                    :rules="rules.input"
                    :error-messages="errors.name"
                    label="Name"
                    icon="book"
                    hideDetails="auto"
                />
                <SoliText
                    v-model="form.description"
                    :disabled="!!product?.id && !product?.physical"
                    :rules="rules.input"
                    :error-messages="errors.description"
                    label="Description"
                    icon="description"
                    hideDetails="auto"
                />
                <v-currency-field
                    v-model="form.price"
                    :rules="priceRules"
                    :error-messages="errors.price"
                    outlined
                    dense
                    class="soli-round"
                    label="Price"
                    error-message="errors.amount"
                    prepend-inner-icon="attach_money"
                    hide-details="auto"
                ></v-currency-field>
                <v-file-input
                    v-if="
                        (!product?.id || product?.physical) &&
                        (showFileInput || !product?.photo)
                    "
                    ref="fileInput"
                    v-model="form.image"
                    :error-messages="errors.image"
                    :rules="imageRules"
                    outlined
                    dense
                    hide-details="auto"
                    prepend-inner-icon="image"
                    label="Image"
                    prepend-icon=""
                    @change="convertPhoto($event)"
                    @click:clear="showImageEditor = false"
                ></v-file-input>
                <v-text-field
                    v-if="!showFileInput && product?.photo"
                    :value="product?.photo?.split('/product-images/')[1]"
                    :error-messages="errors.image"
                    outlined
                    clearable
                    dense
                    hide-details="auto"
                    prepend-inner-icon="image"
                    label="Image"
                    prepend-icon=""
                    @focus="selectNewPhoto()"
                ></v-text-field>
                <v-img
                    v-if="product?.photo && !showFileInput && false"
                    width="200"
                    height="200"
                    :src="
                        product.imagePath
                            ? `/api/image-service/400x400/${product.imagePath}`
                            : product.photo
                    "
                    class="cursor-pointer soli-round"
                    @click="selectNewPhoto()"
                />
                <div v-if="showImageEditor" class="soli-round">
                    <v-container v-if="cropperLoading">
                        <v-skeleton-loader class="soli-round" type="image" />
                    </v-container>
                    <VueCropper
                        v-show="!cropperLoading"
                        ref="cropper"
                        :src="null"
                        :viewMode="1"
                        :aspectRatio="1"
                        :zoomable="false"
                        :autoCropArea="1"
                        :containerStyle="{ height: '200px' }"
                        alt="Loading"
                    />
                </div>
            </div>
        </template>
    </SoliForm>
</template>

<script>
import SoliForm from "./../Base/SoliForm.vue";
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";

export default {
    props: {
        product: {
            type: Object,
            default: {},
        },
        category: {
            type: Object,
            default: { singular: "Product", title: "Products" },
        },
        errors: {
            type: Object,
            default: {},
        },
    },
    components: { VueCropper },
    data() {
        return {
            form: this.$inertia.form({
                category: this.category,
                name: this.product?.name ?? null,
                description: this.product?.description ?? null,
                price: this.product?.price
                    ? this.product.price.unit_amount / 100
                    : null,
                priceId: this.product?.price?.id ?? null,
                image: null,
            }),
            formCopy: null,
            editing: !!this.product?.id,
            edited: !this.product?.id,
            valid: false,
            rules: SoliForm.formRules,
            cropperLoading: false,
            showFileInput: false,
            showImageEditor: false,
            imageLoading: false,
            imageRules: [],
            priceRules: [
                (v) => !v || v > 0 || "Price must be greater than $0",
                (v) => !v || v <= 500 || "Price can't be greater than $500",
                (v) => (v || "").length > 0 || "Price is required",
            ],
        };
    },
    computed: {
        getImageSource() {
            if (this.showFileInput) return null;
            if (!this.product?.id) return null;
            return this.product.imagePath
                ? `/api/image-service/400x400/${this.product.imagePath}`
                : this.product.photo;
        },
    },
    methods: {
        async submit() {
            await this.addPhotoValidation();
            if (!this.valid) {
                this.$refs.form.loading = false;
                return;
            }

            if (this.form.image) {
                await this.$refs.cropper.getCroppedCanvas().toBlob((blob) => {
                    this.form.image = blob;
                });
            }

            const formData = {
                ...this.form.data(),
                price: Math.round(this.form.price * 100),
                category: this.form.category.slug,
            };

            const options = {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    this.showImageEditor = false;
                    this.$refs.form.reset();
                    this.$emit("success");
                },
                onError: (errors) => {
                    console.log(
                        "Form returned errors: " + JSON.stringify(errors)
                    );
                    this.$refs.form.loading = false;
                },
            };

            if (this.editing) {
                this.$inertia.post(
                    `/products/${this.product.id}`,
                    {
                        _method: "PUT",
                        ...formData,
                    },
                    options
                );
            } else {
                this.$inertia.post("/products", formData, options);
            }
        },
        async addPhotoValidation() {
            this.imageRules = this.rules.select;
        },
        selectNewPhoto() {
            this.showFileInput = true;
            this.$nextTick(() => {
                this.$refs.fileInput.$refs.input.click();
                this.cropperLoading = true;
            });
        },
        convertPhoto(file) {
            if (!file) return;
            this.showImageEditor = true;
            let reader = new FileReader();
            reader.onload = (e) => {
                let img = new Image();
                img.src = e.target.result;
                img.onload = () => {
                    this.resizeImage(file, img);
                };
            };

            reader.readAsDataURL(file);
        },
        resizeImage(file, img) {
            let canvas = document.createElement("canvas");
            let ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            let maxWidth = 1000;
            let maxHeight = 1000;
            let width = img.width;
            let height = img.height;
            let aspectRatio = Math.min(maxWidth / width, maxHeight / height);

            let adjustedWidth = width * aspectRatio;
            let adjustedHeight = height * aspectRatio;

            canvas.width = adjustedWidth;
            canvas.height = adjustedHeight;
            ctx.drawImage(img, 0, 0, adjustedWidth, adjustedHeight);
            let dataUrl = canvas.toDataURL(file.type);
            this.$refs.cropper.replace(dataUrl);
            this.cropperLoading = false;
        },
    },
    watch: {
        form: {
            handler: function (val) {
                if (!this.formCopy) return;
                if (JSON.stringify(val.data()) == this.formCopy) {
                    this.edited = false;
                } else {
                    this.edited = true;
                }
            },
            deep: true,
        },
    },
    mounted() {
        if (!this.product.id) return;
        this.formCopy = JSON.stringify(this.form.data());
    },
};
</script>
